/**
 * Core Snakes and Ladders Game Types
 *
 * All TypeScript types and interfaces for the Snakes and Ladders game.
 * Follows the design specified in .kiro/specs/snakes-and-ladders-game/design.md
 *
 * Board: 10×10 grid (positions 1–100), boustrophedon numbering.
 * Standard layout: 10 snakes, 9 ladders.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** A snake (head → tail) or ladder (base → top) on the board */
export interface SnakeOrLadder {
  start: number; // Snake head or ladder base
  end: number;   // Snake tail or ladder top
  type: 'snake' | 'ladder';
}

/** Standard board: 10 snakes */
export const SNAKES: SnakeOrLadder[] = [
  { start: 16, end: 6, type: 'snake' },
  { start: 47, end: 26, type: 'snake' },
  { start: 49, end: 11, type: 'snake' },
  { start: 56, end: 53, type: 'snake' },
  { start: 62, end: 19, type: 'snake' },
  { start: 64, end: 60, type: 'snake' },
  { start: 87, end: 24, type: 'snake' },
  { start: 93, end: 73, type: 'snake' },
  { start: 95, end: 75, type: 'snake' },
  { start: 98, end: 78, type: 'snake' },
];

/** Standard board: 9 ladders */
export const LADDERS: SnakeOrLadder[] = [
  { start: 1, end: 38, type: 'ladder' },
  { start: 4, end: 14, type: 'ladder' },
  { start: 9, end: 31, type: 'ladder' },
  { start: 21, end: 42, type: 'ladder' },
  { start: 28, end: 84, type: 'ladder' },
  { start: 36, end: 44, type: 'ladder' },
  { start: 51, end: 67, type: 'ladder' },
  { start: 71, end: 91, type: 'ladder' },
  { start: 80, end: 100, type: 'ladder' },
];

// ============================================================================
// Player Types
// ============================================================================

export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow';
export type PlayerType = 'human' | 'ai';
export type SnakesLaddersDifficulty = 'easy' | 'medium' | 'hard';

export interface PlayerState {
  id: string;
  color: PlayerColor;
  position: number;        // 0 = off board, 1–100 on board
  type: PlayerType;
  aiDifficulty?: SnakesLaddersDifficulty;
}

// ============================================================================
// Move Types
// ============================================================================

export interface MoveResult {
  player: PlayerColor;
  diceValue: number;
  fromPosition: number;
  toPosition: number;       // After dice move (before snake/ladder)
  finalPosition: number;    // After snake/ladder
  snakeOrLadder?: SnakeOrLadder;
  bouncedBack: boolean;
  extraTurn: boolean;       // Rolled a 6
  won: boolean;
  forfeitedTurn: boolean;   // Three consecutive 6s
  timestamp: number;
}

// ============================================================================
// Game State
// ============================================================================

export interface SnakesLaddersGameState {
  players: PlayerState[];
  currentPlayerIndex: number;
  moveHistory: MoveResult[];
  gameStatus: 'active' | 'completed';
  winner?: PlayerColor;
  consecutiveSixes: number;
  extraTurnEnabled: boolean;
  gameId: string;
  startedAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface SnakesLaddersGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  playerCount: number;       // 2–4
  aiDifficulty?: SnakesLaddersDifficulty;
  aiOpponentCount?: number;  // 1–3
  extraTurnOnSix: boolean;   // Default: true
}

// ============================================================================
// Board Position (for rendering)
// ============================================================================

export interface BoardPosition {
  number: number;  // 1–100
  row: number;     // 0–9 (0 = bottom row)
  col: number;     // 0–9
}

// ============================================================================
// Statistics
// ============================================================================

export interface SnakesLaddersGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageMoves: number;
  snakesHit: number;
  laddersClimbed: number;
}

export interface SnakesLaddersStatistics {
  userId?: string;
  singlePlayer: {
    easy: SnakesLaddersGameStats;
    medium: SnakesLaddersGameStats;
    hard: SnakesLaddersGameStats;
  };
  localMultiplayer: SnakesLaddersGameStats;
  onlineMultiplayer: SnakesLaddersGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface SnakesLaddersGameResult {
  gameId: string;
  mode: SnakesLaddersGameSettings['mode'];
  difficulty?: SnakesLaddersDifficulty;
  winner?: PlayerColor;
  totalMoves: number;
  snakesHit: number;
  laddersClimbed: number;
  duration: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface SnakesLaddersGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface SnakesLaddersGameModeSelectorProps {
  onStartGame: (settings: SnakesLaddersGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const BOARD_SIZE = 10;
export const TOTAL_SQUARES = 100;
export const PLAYER_COLORS: PlayerColor[] = ['red', 'blue', 'green', 'yellow'];
export const MAX_CONSECUTIVE_SIXES = 3;
